// Internal
import { appStore } from '@/store';
import { logger } from '@/lib/logger';
import { getCurrencyConversionData } from './getCurrencyConversionData';
import {
  isConversionNeeded as getIsConversionNeeded,
  isBuyerFxEligible,
} from '@/lib/utils/currencyConversion';
import { PREPARE_CURRENCY_CONVERSION_PAYLOAD } from '@/lib/utils/currencyConversion/fpti';
import { CI } from '@/lib/customerInteractions';

export const conditionallyFetchConversionData = (
  nextFocusedEl: HTMLInputElement | null | undefined,
  viaShippingCallback = false,
): undefined | Promise<void> => {
  const { form: formState, ui: uiState } = appStore.getState();
  const isConversionNeeded: boolean = getIsConversionNeeded();
  const isCardNumberValid: boolean = formState?.cardNumber?.isValid;
  const isConversionShown: boolean = uiState?.showCardConversionOptions;
  const isDccEligible: boolean = isBuyerFxEligible();

  // Return early if we don't need to show the conversion options
  // or if the conversion options are already shown
  if (
    !isConversionNeeded ||
    !isCardNumberValid ||
    (isConversionShown && !viaShippingCallback) ||
    !isDccEligible
  ) {
    logger.info('skip_fetch_card_conversion_call', {
      isCardNumberValid,
      isConversionNeeded,
      isConversionShown,
      isDccEligible,
    });
    return;
  }

  const { ui: uiActions } = appStore.getActions();
  // The `conditionallyFetchConversionData` operation is used by multiple
  // components and some of those components are handling their own loading
  // spinners. The issue is that we have a variety of loading spinners. Hence,
  // we don't want to trigger the default form submission spinner for the
  // shipping callbacks to avoid showing users two different spinners.
  // Ideally, we should have a unified loading spinner.
  // @TODO: unify loading spinners
  if (!viaShippingCallback) {
    uiActions.toggleIsFormSubmitting(true);
  }
  logger.info('fetching_currency_conversion_data');

  // Any errors returned by this API are swallowed since it doesn't
  // block the transaction. That logic is part of `getCurrencyConversionData()`
  return getCurrencyConversionData().then(conversionData => {
    uiActions.toggleIsFormSubmitting(false);

    // If an element to focus on after the call is completed
    // has been passed, focus on that element
    nextFocusedEl?.focus?.();

    const payload = PREPARE_CURRENCY_CONVERSION_PAYLOAD(conversionData);
    logger.fpti('prepare_currency_conversion_options', payload);

    // If the user cannot change the conversion type then we don't
    // display the conversion options
    if (conversionData?.canChangeConversionType) {
      CI.start(CI.Event.CURRENCY_CONVERSION_RENDER);
      uiActions.setShowCardConversionOptions(true);
    }
  });
};
